import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import {
  TextField,
  IconButton,
  useTheme,
  Box,
  Chip,
  Typography,
  Tooltip,
  alpha,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import {
  EditOutlined as Edit,
  SaveOutlined as Save,
  AccessTime as TimeIcon,
  ExpandMore as ExpandIcon,
} from "@mui/icons-material";
import { useSettings } from "../../Contexts/SettingsContext";
import { useTranslation } from "react-i18next";

const EMPTY_ACTIVITY_DATA = { date: "", time: "", remarks: {} };

const RemarksFields = memo(function RemarksFields({
  activity,
  schemaRemarks,
  draftRemarks,
  isEditable,
  onChange,
  onBlur,
}) {
  const { t } = useTranslation("statementoffact");

  if (schemaRemarks && typeof schemaRemarks === "object") {
    return (
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        {Object.keys(schemaRemarks).map((key) => (
          <Grid key={key} size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              label={t(key) || key.toUpperCase()}
              value={draftRemarks?.[key] || ""}
              disabled={!isEditable}
              onChange={(e) => onChange(activity, `remarks.${key}`, e.target.value)}
              onBlur={onBlur}
            />
          </Grid>
        ))}
      </Grid>
    );
  }

    return (
      <TextField
        fullWidth
        size="small"
        multiline
        rows={2}
        label="Remarks"
        value={typeof draftRemarks === "string" ? draftRemarks : ""}
        disabled={!isEditable}
        onChange={(e) => onChange(activity, "remarks", e.target.value)}
      onBlur={onBlur}
      sx={{ mt: 1 }}
    />
  );
});

const ActivityCard = ({
  activity,
  data,
  isEditable,
  onToggle,
  onUpdate,
  onSetNow,
  getLabel,
  expanded,
  onAccordionChange,
  schema,
  readOnly,
  status,
}) => {
  const { t } = useTranslation("statementoffact");
  const { is24Hour } = useSettings();
  const theme = useTheme();

  const safeData = data || EMPTY_ACTIVITY_DATA;
  const [draftData, setDraftData] = useState(safeData);
  const pendingChangesRef = useRef({});
  const flushTimeoutRef = useRef(null);

  useEffect(() => {
    setDraftData(data || EMPTY_ACTIVITY_DATA);
  }, [data]);

  useEffect(
    () => () => {
      if (flushTimeoutRef.current) {
        clearTimeout(flushTimeoutRef.current);
      }
    },
    []
  );

  const statusConfig = {
    empty: {
      label: "Empty",
      chipSx: {
        bgcolor: alpha(theme.palette.text.secondary, 0.1),
        color: "text.secondary",
      },
    },
    editing: {
      label: "Open",
      chipSx: {
        bgcolor: alpha(theme.palette.warning.main, 0.12),
        color: "warning.dark",
      },
    },
    saved: {
      label: "Saved",
      chipSx: {
        bgcolor: alpha(theme.palette.success.main, 0.12),
        color: "success.dark",
      },
    },
  };
  const currentStatus = statusConfig[status] || statusConfig.empty;

  const flushPendingChanges = useCallback(() => {
    const entries = Object.entries(pendingChangesRef.current);

    if (flushTimeoutRef.current) {
      clearTimeout(flushTimeoutRef.current);
      flushTimeoutRef.current = null;
    }

    if (!entries.length) return;

    pendingChangesRef.current = {};
    entries.forEach(([field, value]) => {
      onUpdate(activity, field, value);
    });
  }, [activity, onUpdate]);

  const queueChange = useCallback(
    (field, value, { immediate = false } = {}) => {
      pendingChangesRef.current = {
        ...pendingChangesRef.current,
        [field]: value,
      };

      if (immediate) {
        flushPendingChanges();
        return;
      }

      if (flushTimeoutRef.current) {
        clearTimeout(flushTimeoutRef.current);
      }

      flushTimeoutRef.current = setTimeout(() => {
        flushPendingChanges();
      }, 150);
    },
    [flushPendingChanges]
  );

  const updateDraftValue = useCallback(
    (targetActivity, field, value, options) => {
      setDraftData((prev) => {
        const next = { ...prev };
        if (field.startsWith("remarks.")) {
          const remarkKey = field.split(".")[1];
          next.remarks = {
            ...(prev?.remarks || {}),
            [remarkKey]: value,
          };
        } else {
          next[field] = value;
        }
        return next;
      });

      const shouldSyncImmediately =
        field === "remarks.shipfig" || field === "remarks.blfig";

      queueChange(field, value, {
        ...options,
        immediate: shouldSyncImmediately || options?.immediate,
      });
    },
    [queueChange]
  );

  return (
    <Accordion
      elevation={0}
      expanded={!!expanded}
      onChange={(_event, isExpanded) => onAccordionChange(activity, isExpanded)}
      sx={{
        mb: 1.5,
        borderRadius: "12px !important",
        border: "1px solid",
        borderColor:
          status === "saved"
            ? alpha(theme.palette.success.main, 0.35)
            : isEditable
              ? "primary.main"
              : "divider",
        bgcolor:
          status === "saved"
            ? alpha(theme.palette.success.main, 0.03)
            : isEditable
              ? alpha(theme.palette.primary.main, 0.02)
              : "background.paper",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary expandIcon={<ExpandIcon />}>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}
        >
          <Typography variant="subtitle1" fontWeight="800" sx={{ flexGrow: 1 }}>
            {getLabel(activity)}
          </Typography>
          <Chip
            size="small"
            label={currentStatus.label}
            sx={{
              fontWeight: 800,
              borderRadius: 999,
              ...currentStatus.chipSx,
            }}
          />
          <Box
            role="button"
            tabIndex={readOnly ? -1 : 0}
            onClick={(e) => {
              e.stopPropagation();
              flushPendingChanges();
              onToggle(activity);
            }}
            onKeyDown={(e) => {
              if (readOnly) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                flushPendingChanges();
                onToggle(activity);
              }
            }}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 40,
              height: 32,
              px: 1,
              borderRadius: 1.5,
              cursor: readOnly ? "default" : "pointer",
              bgcolor: isEditable ? "success.main" : "transparent",
              color: isEditable ? "white" : "inherit",
              opacity: readOnly ? 0.5 : 1,
              "&:hover": {
                bgcolor:
                  readOnly
                    ? "transparent"
                    : isEditable
                      ? "success.dark"
                      : alpha(theme.palette.text.primary, 0.04),
              },
            }}
          >
            {isEditable ? <Save fontSize="small" /> : <Edit fontSize="small" />}
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, px: 3, pb: 3 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <DatePicker
              label={t("tableheaddate")}
              value={draftData.date ? dayjs(draftData.date) : null}
              onChange={(val) =>
                updateDraftValue(
                  activity,
                  "date",
                  val ? dayjs(val).format("YYYY-MM-DD") : "",
                  { immediate: true }
                )
              }
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  disabled: !isEditable,
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TimePicker
                label={t("tableheadtime")}
                value={draftData.time ? dayjs(draftData.time, "HH:mm") : null}
                onChange={(val) =>
                  updateDraftValue(
                    activity,
                    "time",
                    val ? dayjs(val).format("HH:mm") : "",
                    { immediate: true }
                  )
                }
                ampm={!is24Hour}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    disabled: !isEditable,
                  },
                }}
              />
              <Tooltip title="Set current time">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => {
                      flushPendingChanges();
                      onSetNow(activity);
                    }}
                    disabled={!isEditable}
                    color="primary"
                  >
                    <TimeIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Grid>
          <Grid size={12}>
            <RemarksFields
              activity={activity}
              schemaRemarks={schema?.remarks}
              draftRemarks={draftData.remarks}
              isEditable={isEditable}
              onChange={updateDraftValue}
              onBlur={flushPendingChanges}
            />
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

const areEqual = (prevProps, nextProps) =>
  prevProps.activity === nextProps.activity &&
  prevProps.data === nextProps.data &&
  prevProps.isEditable === nextProps.isEditable &&
  prevProps.expanded === nextProps.expanded &&
  prevProps.schema === nextProps.schema &&
  prevProps.readOnly === nextProps.readOnly &&
  prevProps.status === nextProps.status &&
  prevProps.getLabel === nextProps.getLabel &&
  prevProps.onToggle === nextProps.onToggle &&
  prevProps.onUpdate === nextProps.onUpdate &&
  prevProps.onSetNow === nextProps.onSetNow &&
  prevProps.onAccordionChange === nextProps.onAccordionChange;

export default memo(ActivityCard, areEqual);
